import { useState } from 'react';

import { monitorApi } from '../../api/monitor';
import type { LineNumber } from '../../api/monitor';
import { Pagination } from '../../components/ui/Pagination';
import { ErrorCard, LoadingCard } from '../../components/ui/ScreenFallback';
import { Toast, useToast } from '../../components/ui/Toast';
import { lineKey, useNumbers } from '../../hooks/useNumbers';
import { pageLabel, syncMessage } from '../../lib/lines';
import { LineDetailSheet } from './LineDetailSheet';
import { LineFilters } from './LineFilters';
import { LineRow } from './LineRow';

/**
 * Tela de Linhas: as linhas que as rotinas diárias leem das operadoras
 * (`line_data_consumptions`), paginadas no servidor, com filtro por operadora e
 * busca por número ou nome do master. Toda linha tem o botão de sincronizar,
 * com ou sem cadastro em `numbers`.
 *
 * O botão de sincronizar leva ao `/numbers/sync` do monitor com a operadora e
 * o número. Ele atualiza a franquia da linha no SGC e escolhe o caminho pela
 * operadora: a Vivo é consultada na hora, a Claro vem da
 * importação diária das 08:00. A diferença aparece no aviso, não em dois
 * botões — para quem usa a tela a pergunta é sempre "qual é o consumo agora".
 *
 * O controller nasce aqui dentro, e não no App como o das APIs: a listagem só
 * interessa a esta tela, e subir o hook faria o app buscar linhas já no login.
 */
export function LinesScreen({ onNotified }: { onNotified?: () => void }) {
  const controller = useNumbers();
  const { page, operators, masters, operatorCode, selectedMasters, query, segments, loading, error, syncing } =
    controller;
  const { toast, show } = useToast();
  const [exporting, setExporting] = useState(false);
  const [openLine, setOpenLine] = useState<LineNumber | null>(null);

  // A planilha leva os filtros da tela, sem paginação — é a relação por linha
  // para conferir contra o portal da operadora e o SGC.
  const handleExport = async () => {
    setExporting(true);
    try {
      await monitorApi.exportNumbers({
        operatorCode,
        line: query,
        masters: selectedMasters,
        status: controller.status,
        groups: controller.segments,
      });
    } catch (cause) {
      show((cause as Error).message, 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleSync = async (number: LineNumber) => {
    try {
      const { text, kind } = syncMessage(await controller.syncLine(number));
      show(text, kind);
    } catch (cause) {
      // O servidor manda o motivo da operadora; ele diz mais que um aviso nosso.
      show((cause as Error).message, 'error');
    } finally {
      // A sincronização vira notificação; sem isto o sino só acenderia no
      // próximo refresh automático.
      onNotified?.();
    }
  };

  // Só a primeira carga bloqueia a tela; trocar de página ou filtrar mantém a
  // lista anterior no lugar, senão a tela pisca a cada tecla digitada.
  if (loading && !page && !error) {
    return <LoadingCard message="Carregando as linhas…" />;
  }

  if (error && !page) {
    return (
      <ErrorCard
        title="Não foi possível carregar as linhas."
        detail={error}
        onRetry={controller.reload}
      />
    );
  }

  return (
    <>
      <LineFilters
        operators={operators}
        masters={masters}
        selected={operatorCode}
        selectedMasters={selectedMasters}
        status={controller.status}
        segments={segments}
        query={query}
        onSelect={controller.setOperator}
        onMastersChange={controller.setSelectedMasters}
        onStatusChange={controller.setStatus}
        onSegmentsChange={controller.setSegments}
        onQueryChange={controller.setQuery}
      />

      <div className="mb-3 flex justify-end">
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="border-line-300 text-navy-800 hover:border-navy-800 hover:bg-surface-100 rounded-lg border bg-white px-3.5 py-2 text-[12.5px] font-bold disabled:opacity-50"
        >
          {exporting ? 'Gerando planilha…' : 'Exportar (XLSX)'}
        </button>
      </div>

      {error ? (
        <div className="border-danger-200 bg-danger-100 text-danger-600 mb-3 rounded-xl border px-4 py-3 text-[12.5px] font-semibold">
          {error}
        </div>
      ) : null}

      <div
        className={`border-line-200 overflow-hidden rounded-[14px] border bg-white ${
          loading ? 'opacity-60' : ''
        }`}
      >
        {page && page.numbers.length > 0 ? (
          page.numbers.map((number) => (
            <LineRow
              key={lineKey(number)}
              number={number}
              syncing={syncing === lineKey(number)}
              busy={syncing !== null}
              onSync={handleSync}
              onOpen={() => setOpenLine(number)}
            />
          ))
        ) : (
          <div className="text-ink-600 p-7 text-center text-[13.5px]">
            {query.trim() || operatorCode || selectedMasters.length || segments.length
              ? 'Nenhuma linha encontrada para este filtro.'
              : 'Nenhuma linha cadastrada.'}
          </div>
        )}
      </div>

      {page ? (
        <Pagination
          page={page.page}
          totalPages={page.totalPages}
          label={pageLabel(page.page, page.pageSize, page.total)}
          disabled={loading}
          onChange={controller.goToPage}
        />
      ) : null}

      {openLine ? (
        <LineDetailSheet line={openLine} onClose={() => setOpenLine(null)} />
      ) : null}

      <Toast message={toast} />
    </>
  );
}
